import { Button } from '@/global/components/Button'
import { useThemeStore } from '@/global/components/ToggleTheme'
import { cn } from '@/lib/utils'
import { EventClickArg } from '@fullcalendar/core/index.js'
import dayGridPlugin from '@fullcalendar/daygrid'
import FullCalendar from '@fullcalendar/react'
import dayjs from 'dayjs'
import { useCompetencia } from 'global/components/Selects/SelectCompetencia'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { TabelaHorarioFuncionario } from 'service/api'
import { match } from 'ts-pattern'
import { generateColor } from '../utils'
import './calendario.css'
import { AdicionarTabelaHorarioFuncionario } from './modals/Adicionar'
import { EditarTabelaHorarioFuncionario } from './modals/ExcluirEditar'

interface IProps {
    value: ReturnType<typeof useCompetencia>[0]
    onChange: ReturnType<typeof useCompetencia>[1]
    tabelas?: TabelaHorarioFuncionario[]
    editable?: boolean
}

export default function CalendarioTabelasHorario({
    value: competencia,
    onChange,
    tabelas = [],
    editable = true,
}: IProps) {
    const [open, setOpen] = useState(false)
    const [tabelaEditar, setTabelaEditar] =
        useState<TabelaHorarioFuncionario | null>(null)
    const calendarRef = useRef<FullCalendar>(null)
    const mode = useThemeStore(state => state.theme)

    useEffect(() => {
        const calendarApi = (calendarRef.current as any)?.getApi()
        if (calendarApi)
            calendarApi.gotoDate(dayjs(competencia, 'YYYY-MM').toDate())
    }, [competencia])

    const events = tabelas.map((it, index) => {
        const { light, dark } = generateColor(
            `${it.tabelaHorarioId}-${it.TabelaHorario.nome}`,
        )

        const periodo = (() => {
            const start = dayjs.utc(it.dtInicial),
                end = it.dtFinal
                    ? dayjs.utc(it.dtFinal).add(1, 'day')
                    : dayjs.utc(competencia, 'YYYY-MM').add(2, 'month')

            if (start.isSame(end, 'day'))
                return {
                    date: start.format('YYYY-MM-DD'),
                }

            return {
                start: start.format('YYYY-MM-DD'),
                end: end?.format('YYYY-MM-DD'),
            }
        })()

        return {
            id: it.id?.toString() || index.toString(),
            title: it.TabelaHorario.nome,
            color: mode === 'dark' ? dark : light,
            textColor: mode === 'dark' ? 'white' : 'black',
            allDay: true,
            ...periodo,
        }
    })

    function handleEventClick(clickInfo: EventClickArg) {
        const tabela =
            tabelas.find(it => it.id.toString() === clickInfo.event.id) ?? null
        if (!tabela) return
        setTabelaEditar(tabela)
    }

    return [
        <div className="max-h-max size-full flex flex-col rounded-lg overflow-x-hidden overflow-y-visible border">
            <div className="w-full h-fit border rounded-t-lg border-b-0">
                <div className="inline-flex justify-between items-center overflow-x-auto gap-1 w-full p-2">
                    <div className="flex items-center gap-4">
                        <div
                            className={cn(
                                'flex flex-row items-center',
                                '*:rounded-none *:first:rounded-l-sm *:last:rounded-r-sm',
                                '*:h-10 *:aspect-1/1 *:p-2',
                            )}
                        >
                            <Button
                                onClick={() =>
                                    onChange(prev =>
                                        dayjs(prev)
                                            .subtract(1, 'month')
                                            .format('YYYY-MM'),
                                    )
                                }
                                title="Mês anterior"
                                variant="outlined"
                                size="small"
                            >
                                <ChevronLeftIcon />
                            </Button>
                            <Button
                                onClick={() =>
                                    onChange(prev =>
                                        dayjs(prev)
                                            .add(1, 'month')
                                            .format('YYYY-MM'),
                                    )
                                }
                                title="Próximo mês"
                                variant="outlined"
                                size="small"
                            >
                                <ChevronRightIcon />
                            </Button>
                        </div>
                        <span className="text-lg font-semibold truncate">
                            {dayjs(competencia).format('MMMM [de] YYYY')}
                        </span>
                    </div>
                    {editable && (
                        <Button
                            variant="contained"
                            onClick={() => setOpen(true)}
                            className="size-10"
                        >
                            <PlusIcon className="size-8 stroke-3" />
                        </Button>
                    )}
                </div>
            </div>
            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin]}
                initialView="dayGridMonth"
                events={events}
                eventContent={eventInfo =>
                    renderEventContent(eventInfo, tabelas)
                }
                eventOrder={(a: any, b: any) => {
                    const indexA = tabelas.findIndex(
                        it => it.id.toString() === a.id,
                    )
                    const indexB = tabelas.findIndex(
                        it => it.id.toString() === b.id,
                    )
                    return indexA - indexB
                }}
                dayMaxEvents={1}
                locale={'pt-br'}
                headerToolbar={false}
                eventClick={handleEventClick}
                defaultAllDay
                timeZone="UTC"
                moreLinkContent={renderMoreLinkContent}
                moreLinkDidMount={info => {
                    // Add event listener to center popup on screen
                    const moreLink = info.el
                    moreLink.addEventListener('click', () => {
                        setTimeout(() => {
                            const popover = document.querySelector(
                                '.fc-popover',
                            ) as HTMLElement
                            if (popover) {
                                const viewportWidth = window.innerWidth
                                const viewportHeight = window.innerHeight
                                const popoverRect =
                                    popover.getBoundingClientRect()

                                // Center the popover on the screen
                                const left =
                                    (viewportWidth - popoverRect.width) / 2
                                const top =
                                    (viewportHeight - popoverRect.height) / 2

                                popover.style.position = 'fixed'
                                popover.style.left = `${left}px`
                                popover.style.top = `${top}px`
                                popover.style.zIndex = '9999'
                                popover.style.transform = 'none'

                                // Remove positioning classes
                                popover.classList.remove(
                                    'fc-popover-right',
                                    'fc-popover-left',
                                )
                            }
                        }, 50)
                    })
                }}
                dayCellContent={info => {
                    const date = dayjs.utc(info.date)
                    const color = match(date)
                        .when(
                            d => d.day() === 0,
                            () => 'text-red-600' as const,
                        )
                        .when(
                            d => d.day() === 6,
                            () => 'text-red-400' as const,
                        )
                        .when(
                            d => d.isSame(dayjs(), 'day'),
                            () =>
                                'text-inherit bg-primary/40 rounded-md px-1' as const,
                        )
                        .otherwise(() => 'text-inherit' as const)

                    return (
                        <span
                            key={date.toISOString()}
                            className={cn(
                                'text-center text-sm font-semibold mr-2',
                                color,
                            )}
                        >
                            {date.format('D')}
                        </span>
                    )
                }}
                dayHeaderContent={info => {
                    const date = dayjs.utc(info.date)
                    const color = match(date)
                        .when(
                            d => d.day() === 0,
                            () => 'text-red-600',
                        )
                        .when(
                            d => d.day() === 6,
                            () => 'text-red-400',
                        )
                        .when(
                            d => d.isSame(dayjs(), 'day'),
                            () =>
                                'text-inherit bg-primary/40 rounded-md px-1' as const,
                        )
                        .otherwise(() => 'text-inherit' as const)

                    return (
                        <span
                            className={cn(
                                'text-center text-sm font-semibold',
                                color,
                            )}
                            key={date.toISOString()}
                        >
                            {date.format('ddd')}
                        </span>
                    )
                }}
            />
        </div>,
        <EditarTabelaHorarioFuncionario
            open={!!tabelaEditar}
            onClose={() => {
                setTabelaEditar(null)
            }}
            tabela={tabelaEditar!}
            disableFields={!editable}
        />,
        <AdicionarTabelaHorarioFuncionario
            onClose={() => setOpen(false)}
            open={open}
        />,
    ]
}

function renderMoreLinkContent(e: { num: number }) {
    return (
        <div className="fc-more-link-custom px-2 py-1 rounded-md bg-transparent text-muted-foreground text-xs font-medium hover:bg-muted/50 transition-colors">
            +{e.num} ignorado(s)
        </div>
    )
}

function renderEventContent(
    eventInfo: any,
    tabelas: TabelaHorarioFuncionario[] = [],
) {
    const isTrocaAutomatica = tabelas.find(
        it => it.id.toString() === eventInfo.event.id,
    )?.isTrocaAutomatica

    return (
        <div
            className={cn(
                'text-sm truncate px-2 min-w-full text-center cursor-pointer!',
                isTrocaAutomatica ? 'italic' : '',
            )}
            title={
                eventInfo.event.title +
                (isTrocaAutomatica ? ' (Troca automática)' : '')
            }
        >
            {eventInfo.event.title}
        </div>
    )
}
